import { useEffect, useState } from "react";
import { Mail, ChevronDown, ChevronUp, Users, ShoppingCart, Briefcase } from "lucide-react";

const FAQPage = () => {
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const askSection = document.getElementById("ask-anything");
      if (askSection) {
        askSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const toggleSection = (section: any) => {
    setOpenSection(openSection === section ? null : section);
  };

  const faqSections = [
    {
      id: 'buyers',
      title: 'For Buyers',
      icon: ShoppingCart,
      questions: [
        {
          q: "How do I buy a product?",
          a: "Search for a listing, connect with the seller, and use our secure escrow system for payment."
        },
        {
          q: "Is payment safe?",
          a: "Yes, payments are held in escrow until you confirm the product/service is received."
        },
        {
          q: "Can I report a seller?",
          a: "Yes, you can contact support directly over email Id, we will mark the seller after proper review."
        }
      ]
    },
    {
      id: 'sellers',
      title: 'For Sellers',
      icon: Briefcase,
      questions: [
        {
          q: "How do I list a product or service?",
          a: "Go to your dashboard and click \"Add Listing.\" Choose product or service accordingly."
        },
        {
          q: "How do I get paid?",
          a: "After successful delivery and buyer confirmation, the escrow will release funds to your account."
        },
        {
          q: "Are there any fees?",
          a: "We charge a small transaction fee upon successful sale. You'll see it clearly during payout."
        }
      ]
    },
    {
      id: 'bizz',
      title: 'For Marketing & Skill Users',
      icon: Users,
      questions: [
        {
          q: "Can I post job or service offers?",
          a: "Yes, Busiconn supports posting of skills, freelance services, and business offers in the relevant section."
        },
        {
          q: "How do I get clients?",
          a: "Keep your profile active, share listings, and engage with businesses through chat or calls."
        },
        {
          q: "Can I promote my business?",
          a: "Yes! You can post product/service deals and promote your business page on Busiconn for visibility."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 text-lg">
            Find answers to common questions about Busiconn
          </p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6 mb-16">
          {faqSections.map((section) => (
            <div
              key={section.id}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
            >
              {/* Section Header */}
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white flex items-center justify-between hover:from-blue-600 hover:to-purple-700 transition-all duration-200"
              >
                <div className="flex items-center gap-3">
                  <section.icon className="h-6 w-6" />
                  <h2 className="text-xl font-semibold">{section.title}</h2>
                </div>
                {openSection === section.id ? (
                  <ChevronUp className="h-5 w-5" />
                ) : (
                  <ChevronDown className="h-5 w-5" />
                )}
              </button>

              {/* Section Content */}
              {openSection === section.id && (
                <div className="px-6 py-4 animate-in slide-in-from-top-2 duration-200">
                  <div className="space-y-6">
                    {section.questions.map((item, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <h3 className="font-semibold text-gray-800 mb-2">
                          {item.q}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {item.a}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Interactive Support Hub */}
        <div id="ask-anything" className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-teal-600 px-6 py-4">
            <div className="flex items-center gap-3 text-white">
              <Mail className="h-6 w-6" />
              <h2 className="text-2xl font-bold">Get Support - Multiple Ways!</h2>
            </div>
          </div>
          
          <div className="px-6 py-6">
            <p className="text-gray-600 mb-8 leading-relaxed">
              Choose your preferred way to get help. We're here to support your business success!
            </p>
            
            {/* Support Options Grid */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* WhatsApp Business Support */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">WhatsApp Business</h3>
                    <p className="text-sm text-gray-600">Quick responses, file sharing</p>
                  </div>
                </div>
                <p className="text-gray-700 mb-4 text-sm">
                  Get instant support, share screenshots, and discuss business opportunities directly.
                </p>
                <a
                  href="https://wa.me/919110067500?text=Hi%20Busiconn%20Team,%20I%20have%20a%20question%20about%20the%20platform."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.886 3.75"/>
                  </svg>
                  Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-8 text-gray-500">
          <p>Need immediate help? Email us directly at <strong>contact@busiconn.com</strong></p>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;